import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/LanguageContext";

/**
 * 5 steps. A fresh start.
 * Sticky-scroll vertical timeline with a saffron progress spine that fills as the user scrolls.
 * Step icons rendered in line-art Moroccan geometry (arched doorways, compass rose).
 */

// Line-art Moroccan icons (compass / arch / star / scroll / key)
const StepIcons = {
  Compass: () => (
    <svg viewBox="0 0 64 64" className="w-9 h-9" stroke="#E8A045" strokeWidth="1.1" fill="none">
      <circle cx="32" cy="32" r="22" />
      <circle cx="32" cy="32" r="14" />
      <path d="M 32 10 L 36 32 L 32 54 L 28 32 Z" fill="#E8A045" />
      <path d="M 10 32 L 32 28 L 54 32 L 32 36 Z" opacity="0.5" />
      <circle cx="32" cy="32" r="2.5" fill="#E8A045" />
    </svg>
  ),
  Quill: () => (
    <svg viewBox="0 0 64 64" className="w-9 h-9" stroke="#E8A045" strokeWidth="1.1" fill="none">
      <path d="M 12 52 L 44 20 Q 50 14 52 12 Q 50 18 44 24 L 12 52 Z" />
      <line x1="20" y1="52" x2="44" y2="28" />
      <circle cx="12" cy="52" r="2" fill="#E8A045" />
      <path d="M 22 22 L 36 22 M 22 30 L 32 30" strokeWidth="0.9" />
    </svg>
  ),
  Arch: () => (
    <svg viewBox="0 0 64 64" className="w-9 h-9" stroke="#E8A045" strokeWidth="1.1" fill="none">
      <path d="M 14 54 L 14 28 Q 14 12 32 12 Q 50 12 50 28 L 50 54" />
      <path d="M 22 54 L 22 32 Q 22 22 32 22 Q 42 22 42 32 L 42 54" />
      <circle cx="32" cy="12" r="2" fill="#E8A045" />
      <line x1="6" y1="54" x2="58" y2="54" strokeWidth="1.3" />
    </svg>
  ),
  Envelope: () => (
    <svg viewBox="0 0 64 64" className="w-9 h-9" stroke="#E8A045" strokeWidth="1.1" fill="none">
      <rect x="8" y="16" width="48" height="32" />
      <path d="M 8 16 L 32 36 L 56 16" />
      <path d="M 8 48 L 28 32 M 56 48 L 36 32" opacity="0.5" />
    </svg>
  ),
  Key: () => (
    <svg viewBox="0 0 64 64" className="w-9 h-9" stroke="#E8A045" strokeWidth="1.1" fill="none">
      <circle cx="20" cy="32" r="10" />
      <line x1="28" y1="32" x2="56" y2="32" />
      <line x1="44" y1="32" x2="44" y2="40" />
      <line x1="50" y1="32" x2="50" y2="42" />
      <circle cx="20" cy="32" r="3" fill="#E8A045" />
    </svg>
  ),
};

const ICONS = [StepIcons.Compass, StepIcons.Quill, StepIcons.Arch, StepIcons.Envelope, StepIcons.Key];

const HowItWorks = () => {
  const { t } = useLang();
  const sectionRef = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // Start filling when section top reaches mid viewport, finish when bottom leaves viewport
      const start = vh * 0.85;
      const end = -rect.height + vh * 0.4;
      const raw = (start - rect.top) / (start - end);
      const p = Math.max(0, Math.min(1, raw));
      setProgress(p);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section
      id="how"
      data-testid="how-section"
      ref={sectionRef}
      className="relative bg-[#0D1117] py-28 sm:py-40 overflow-hidden"
    >
      <div className="noise-soft" />

      {/* Eyebrow + brushstroke */}
      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 mb-24">
        <span className="brushstroke mb-6" />
        <div className="font-mono-accent text-[10px] text-[#E8A045] mb-6">
          {t.how.eyebrow}
        </div>
        <h2
          data-testid="how-headline"
          className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] text-[#F5F2EE] leading-[1.02] tracking-tight max-w-4xl"
        >
          {t.how.headline}
        </h2>
      </div>

      {/* Steps + sticky progress spine */}
      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Sticky spine column */}
          <div className="hidden lg:block lg:col-span-1 relative">
            <div className="sticky top-1/3">
              <div className="relative w-px h-72 bg-[#E8A045]/10 mx-auto">
                <div
                  className="absolute top-0 left-0 w-px bg-[#E8A045] transition-all duration-200"
                  style={{ height: `${progress * 100}%` }}
                />
                {/* progress dot */}
                <div
                  className="absolute left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-[#E8A045] transition-all duration-200"
                  style={{
                    top: `calc(${progress * 100}% - 5px)`,
                    boxShadow: "0 0 24px rgba(232,160,69,0.7)",
                  }}
                />
              </div>
              <div className="mt-6 text-center font-mono-accent text-[10px] text-[#E8A045]">
                {Math.round(progress * 100)}%
              </div>
            </div>
          </div>

          {/* Steps stack */}
          <div className="lg:col-span-11 space-y-24 sm:space-y-32">
            {t.how.steps.map((step, i) => {
              const Icon = ICONS[i] || StepIcons.Compass;
              return (
                <motion.article
                  key={i}
                  data-testid={`step-${i}`}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-120px" }}
                  transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                  className="relative grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start"
                >
                  {/* Icon */}
                  <div className="md:col-span-2 flex md:justify-start">
                    <div className="relative">
                      <Icon />
                      {/* subtle saffron ring on hover */}
                      <span className="absolute inset-0 -m-3 rounded-full border border-[#E8A045]/15" />
                    </div>
                  </div>

                  {/* Text */}
                  <div className="md:col-span-10">
                    <div className="font-mono-accent text-[10px] text-[#E8A045] mb-4">
                      0{i + 1} · {step.label || t.how.eyebrow}
                    </div>
                    <h3 className="font-display text-3xl sm:text-4xl lg:text-[3rem] text-[#F5F2EE] leading-[1.05] tracking-tight mb-5 max-w-2xl">
                      {step.title}
                    </h3>
                    <p className="font-body text-base sm:text-lg text-[#F5F2EE]/55 leading-relaxed max-w-xl">
                      {step.desc}
                    </p>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
